import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useGetIntegrationToken } from '../../../api/users'
import { useAbundanceEngineParameters } from '../../../hooks/useAbundanceEngineParameters'
import LoadingView from '../../pages/LoadingView'
import ErrorView from '../../pages/ErrorView'
import SydButton from '../../commonDesign/Button'
import { ICON_NAMES } from '../../../constants'
import IntegrationAuthDialog from '../IntegrationIframe/IntegrationAuthDialog'
import { IntegrationAuthContext } from './IntegrationAuthContext'

const SIZE_CONFIG = {
  sm: {
    titleClass: 'font-size-lg',
    subtitleClass: 'font-size-sm',
    buttonSize: 'md',
    gap: 'gap-1'
  },
  md: {
    titleClass: 'font-size-lg',
    subtitleClass: 'font-size-md',
    buttonSize: 'md',
    gap: 'gap-2'
  },
  lg: {
    titleClass: 'font-size-2xl',
    subtitleClass: 'font-size-lg',
    buttonSize: 'lg',
    gap: 'gap-3'
  }
}

const IntegrationAuthProvider = ({
  integration,
  children,
  allowAuth = true,
  size = 'md',
  authLabels = {
    connectButton: 'Connect',
    buttonIcon: ICON_NAMES.link,
    title: 'Authentication Required',
    subtitle: '',
    modalTitle: 'Connect Integration',
    modalSubtitle: 'Please enter your credentials to connect this integration.'
  },
  errorLabels = {
    title: 'Error loading authentication',
    subtitle: 'An error occurred while checking authentication status. Please try again later.'
  },
  authStyles = {},
  authClasses = {},
  width = '100%',
  height = '100%'
}) => {
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const abundanceParams = useAbundanceEngineParameters()

  const {
    data: tokenData,
    isLoading,
    error,
    refetch
  } = useGetIntegrationToken(integration, 201, abundanceParams.clientId, {
    enabled: !!integration && !!abundanceParams.clientId
  })

  const requiresAuth = useMemo(() => !!error?.response?.requiresAuth, [error])
  const isAuthenticated = useMemo(() => !!tokenData && !requiresAuth, [tokenData, requiresAuth])

  const handleConnectClick = useCallback(() => {
    setAuthDialogOpen(true)
  }, [])

  const handleAuthSuccess = useCallback(() => {
    refetch()
  }, [refetch])

  const contextValue = useMemo(() => ({
    isAuthenticated,
    isLoading,
    tokenData,
    refetchToken: refetch,
    integration
  }), [isAuthenticated, isLoading, tokenData, refetch, integration])

  const sizeConfig = SIZE_CONFIG[size] || SIZE_CONFIG.md

  if (isLoading) {
    return <LoadingView width={width} height={height} />
  }

  if (error && requiresAuth && allowAuth) {
    return (
      <div
        className={`flex flex-col items-center justify-center ${authClasses.container || ''}`}
        style={{ width, height, ...authStyles.container }}
      >
        <div className={`flex flex-col items-center justify-center ${sizeConfig.gap} ${authClasses.content || ''}`} style={authStyles.content}>
          <p
            className={`${sizeConfig.titleClass} text-center my-0 ${authClasses.title || ''}`}
            style={authStyles.title}
          >
            {authLabels.title}
          </p>
          {authLabels.subtitle && (
            <p
              className={`${sizeConfig.subtitleClass} text-center mt-0 mb-4 ${authClasses.subtitle || ''}`}
              style={authStyles.subtitle}
            >
              {authLabels.subtitle}
            </p>
          )}
          <SydButton
            size={sizeConfig.buttonSize}
            variant='primary'
            onClick={handleConnectClick}
            icon={authLabels.buttonIcon}
            className={authClasses.button}
            style={authStyles.button}
          >
            {authLabels.connectButton}
          </SydButton>
        </div>
        <IntegrationAuthDialog
          open={authDialogOpen}
          onClose={() => setAuthDialogOpen(false)}
          onSubmit={handleAuthSuccess}
          inputs={error.response?.authInputs || []}
          integration={integration}
          title={authLabels.modalTitle}
          subtitle={authLabels.modalSubtitle}
        />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorView
        width='100%'
        height='auto'
        error={{
          message: errorLabels.title,
          details: errorLabels.subtitle
        }}
      />
    )
  }

  if (!isAuthenticated) {
    return (
      <ErrorView
        width='100%'
        height='auto'
        error={{
          message: 'Unable to verify authentication status',
          details: 'Please try again later.'
        }}
      />
    )
  }

  return (
    <IntegrationAuthContext.Provider value={contextValue}>
      {children}
    </IntegrationAuthContext.Provider>
  )
}

IntegrationAuthProvider.propTypes = {
  integration: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  allowAuth: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  authLabels: PropTypes.shape({
    connectButton: PropTypes.string,
    buttonIcon: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    modalTitle: PropTypes.string,
    modalSubtitle: PropTypes.string
  }),
  errorLabels: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string
  }),
  authStyles: PropTypes.shape({
    container: PropTypes.object,
    content: PropTypes.object,
    title: PropTypes.object,
    subtitle: PropTypes.object,
    button: PropTypes.object
  }),
  authClasses: PropTypes.shape({
    container: PropTypes.string,
    content: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    button: PropTypes.string
  }),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default IntegrationAuthProvider
